import { ExportResultCode } from '@opentelemetry/core';
import { enqueueKeepaliveFetch } from 'beacon-keepalive-queue/js/index';
export const LOCAL_OTLP_ENDPOINT = '/otlp/v1/traces';

// Browser keepalive requests are capped at 64KB; use 60KB to leave headroom.
export const MAX_KEEPALIVE_BYTES = 60 * 1024;
function hrTimeToNanoString(hrTime) {
  return String(BigInt(hrTime[0]) * 1000000000n + BigInt(hrTime[1]));
}
function toOtlpAttributeValue(value) {
  if (typeof value === 'string') {
    return {
      stringValue: value
    };
  }
  if (typeof value === 'boolean') {
    return {
      boolValue: value
    };
  }
  if (typeof value === 'number') {
    return Number.isInteger(value) ? {
      intValue: String(value)
    } : {
      doubleValue: value
    };
  }
  if (typeof value === 'bigint') {
    return {
      intValue: String(value)
    };
  }
  if (Array.isArray(value)) {
    return {
      arrayValue: {
        values: value.map(v => ({
          value: toOtlpAttributeValue(v)
        }))
      }
    };
  }
  return {
    stringValue: String(value)
  };
}
function toOtlpAttributes(attributes) {
  return Object.entries(attributes).filter(([, v]) => v !== undefined).map(([key, value]) => ({
    key,
    value: toOtlpAttributeValue(value)
  }));
}
export class OtlpExporter {
  constructor(endpoint = LOCAL_OTLP_ENDPOINT, {
    httpClient
  } = {}) {
    this._endpoint = endpoint;
    this._httpClient = httpClient !== null && httpClient !== void 0 ? httpClient : null;
  }
  export(spans, resultCallback) {
    const otlpSpans = spans.map(s => this._toOtlpSpan(s));
    if (!this._httpClient && spans.length > 1 && new TextEncoder().encode(JSON.stringify(otlpSpans)).byteLength > MAX_KEEPALIVE_BYTES) {
      const mid = Math.floor(spans.length / 2);
      let pending = 2;
      let failed = null;
      const onChunkResult = result => {
        if (result.code === ExportResultCode.FAILED) {
          failed = result;
        }
        if (--pending === 0) {
          var _failed;
          resultCallback((_failed = failed) !== null && _failed !== void 0 ? _failed : {
            code: ExportResultCode.SUCCESS
          });
        }
      };
      this.export(spans.slice(0, mid), onChunkResult);
      this.export(spans.slice(mid), onChunkResult);
      return;
    }
    const resourceSpans = this._groupByResource(spans, otlpSpans);
    const body = {
      resourceSpans
    };
    if (this._httpClient) {
      this._httpClient.post(this._endpoint, {
        data: body
      }).then(() => resultCallback({
        code: ExportResultCode.SUCCESS
      })).catch(err => resultCallback({
        code: ExportResultCode.FAILED,
        error: err instanceof Error ? err : new Error(String(err))
      }));
    } else {
      enqueueKeepaliveFetch(this._endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      }, {
        onSuccess: () => resultCallback({
          code: ExportResultCode.SUCCESS
        }),
        onFailure: reason => resultCallback({
          code: ExportResultCode.FAILED,
          error: reason instanceof Error ? reason : new Error(String(reason))
        })
      });
    }
  }
  shutdown() {
    return Promise.resolve();
  }
  _groupByResource(spans, otlpSpans) {
    const resourceMap = new Map();
    for (let i = 0; i < spans.length; i++) {
      const resource = spans[i].resource;
      if (!resourceMap.has(resource)) {
        resourceMap.set(resource, []);
      }
      resourceMap.get(resource).push({
        span: spans[i],
        otlp: otlpSpans[i]
      });
    }
    return Array.from(resourceMap.entries()).map(([resource, pairs]) => ({
      resource: {
        attributes: toOtlpAttributes(resource.attributes)
      },
      scopeSpans: this._groupByScope(pairs)
    }));
  }
  _groupByScope(pairs) {
    const scopeMap = new Map();
    for (const pair of pairs) {
      var _pair$span$instrument;
      const key = `${pair.span.instrumentationScope.name}@${(_pair$span$instrument = pair.span.instrumentationScope.version) !== null && _pair$span$instrument !== void 0 ? _pair$span$instrument : ''}`;
      if (!scopeMap.has(key)) {
        scopeMap.set(key, []);
      }
      scopeMap.get(key).push(pair);
    }
    return Array.from(scopeMap.entries()).map(([, scopePairs]) => {
      var _scope$version;
      const scope = scopePairs[0].span.instrumentationScope;
      return {
        scope: {
          name: scope.name,
          version: (_scope$version = scope.version) !== null && _scope$version !== void 0 ? _scope$version : ''
        },
        spans: scopePairs.map(({
          otlp
        }) => otlp)
      };
    });
  }
  _toOtlpSpan(span) {
    var _span$parentSpanConte;
    const ctx = span.spanContext();
    return {
      traceId: ctx.traceId,
      spanId: ctx.spanId,
      parentSpanId: (_span$parentSpanConte = span.parentSpanContext) === null || _span$parentSpanConte === void 0 ? void 0 : _span$parentSpanConte.spanId,
      name: span.name,
      kind: span.kind + 1,
      startTimeUnixNano: hrTimeToNanoString(span.startTime),
      endTimeUnixNano: hrTimeToNanoString(span.endTime),
      attributes: toOtlpAttributes(span.attributes),
      status: {
        code: span.status.code,
        message: span.status.message
      },
      droppedAttributesCount: span.droppedAttributesCount,
      droppedEventsCount: span.droppedEventsCount,
      droppedLinksCount: span.droppedLinksCount
    };
  }
}